import { oc } from "@orpc/contract";
import { z } from "zod";

// ─── The lines behind a buyer-board line's stock position (GEN-3285) ────────
// The Line footer's Supply tab: the open orders behind On order, the open
// sales orders and staged transfers net takes off, and the open transfers out
// with who receives them (GEN-3284), at the line's destination.
// Served under procurement.replenishmentSuggestions.getLineSupplyDemand.

export const GetLineSupplyDemandInputSchema = z.object({
  suggestionId: z.string().uuid(),
});
export type GetLineSupplyDemandInput = z.infer<
  typeof GetLineSupplyDemandInputSchema
>;

// One open PO line or inbound open TO line, as the engine counts it.
export const LineReceiptSchema = z.object({
  kind: z.enum(["purchase_order", "transfer_order"]),
  // PO or TO header id.
  orderId: z.string().uuid(),
  lineId: z.string().uuid(),
  number: z.string().nullable(),
  // YYYY-MM-DD.
  expectedDate: z.string(),
  quantity: z.number(),
});
export type LineReceipt = z.infer<typeof LineReceiptSchema>;

// One open sales-order line that counts as live demand.
export const LineSalesDemandSchema = z.object({
  salesOrderId: z.string().uuid(),
  lineId: z.string().uuid(),
  number: z.string(),
  // The source's line number on the order (AS400 GDLINENO), when kept.
  lineNumber: z.number().int().nullable(),
  // Delivery date, else order date (YYYY-MM-DD).
  date: z.string().nullable(),
  quantity: z.number(),
  // Who the order is for (GEN-3284): the source's customer name and sold-to
  // account (AS400 GDCNAME / GDSOLDN); null when the source has none.
  customerName: z.string().nullable(),
  customerCode: z.string().nullable(),
  // A product-on-the-fly order (AS400 order type 3): bought for this
  // customer, never stocked.
  productOnTheFly: z.boolean(),
});
export type LineSalesDemand = z.infer<typeof LineSalesDemandSchema>;

// One open AS400 transfer shipping out of the location to another warehouse
// (an internal sales order, GEN-3284). AS400 counts it in the location's
// unshipped quantity, so net available does not take it off again.
export const LineTransferOutSchema = z.object({
  transferOrderId: z.string().uuid(),
  lineId: z.string().uuid(),
  // The transfer number buyers key in AS400, without this location's prefix.
  number: z.string(),
  lineNumber: z.number().int().nullable(),
  // YYYY-MM-DD.
  orderedDate: z.string().nullable(),
  expectedDate: z.string().nullable(),
  // Ordered less shipped.
  quantity: z.number(),
  // The receiving warehouse; all three null when the transfer names none.
  receivingWarehouseId: z.string().uuid().nullable(),
  // The short AS400 location code (e.g. W+OTT); null when not synced.
  receivingWarehouseCode: z.string().nullable(),
  receivingWarehouseName: z.string().nullable(),
  // How many open orders the receiving warehouse has for the item: its open
  // sales orders in net and its own open transfers out. They are the demand
  // that may sit behind this transfer; the buyer opens them under it.
  receivingOpenOrders: z.number().int(),
});
export type LineTransferOut = z.infer<typeof LineTransferOutSchema>;

// One staged draft-transfer line shipping out of the destination.
export const LineStagedTransferSchema = z.object({
  draftTransferOrderId: z.string().uuid(),
  lineId: z.string().uuid(),
  trackingNo: z.string().nullable(),
  toWarehouseName: z.string().nullable(),
  quantity: z.number(),
});
export type LineStagedTransfer = z.infer<typeof LineStagedTransferSchema>;

export const GetLineSupplyDemandResponseSchema = z.object({
  suggestionId: z.string().uuid(),
  destinationWarehouseId: z.string().uuid(),
  // By expected date.
  receipts: z.array(LineReceiptSchema),
  // By date, oldest first.
  salesOrders: z.array(LineSalesDemandSchema),
  stagedTransfers: z.array(LineStagedTransferSchema),
  // By ordered date, oldest first.
  transfersOut: z.array(LineTransferOutSchema),
});
export type GetLineSupplyDemandResponse = z.infer<
  typeof GetLineSupplyDemandResponseSchema
>;

// The same lines for one SKU at one location, without a suggestion: the
// Replenishment detail lines panel (GEN-3241).
export const GetSupplyDemandLinesInputSchema = z.object({
  sku: z.string().trim().min(1).max(255),
  warehouseId: z.string().uuid(),
});
export type GetSupplyDemandLinesInput = z.infer<
  typeof GetSupplyDemandLinesInputSchema
>;

export const GetSupplyDemandLinesResponseSchema =
  GetLineSupplyDemandResponseSchema.omit({
    suggestionId: true,
    destinationWarehouseId: true,
  }).extend({
    sku: z.string(),
    warehouseId: z.string().uuid(),
  });
export type GetSupplyDemandLinesResponse = z.infer<
  typeof GetSupplyDemandLinesResponseSchema
>;

export const getSupplyDemandLinesContract = oc
  .input(GetSupplyDemandLinesInputSchema)
  .output(GetSupplyDemandLinesResponseSchema)
  .route({
    method: "GET",
    summary: "Get the order lines behind one SKU's position at one location",
    description:
      "Returns the open purchase-order and inbound transfer-order lines behind a SKU's on-order quantity at one warehouse, the open sales-order lines (with customer) and staged outbound transfer lines its net available subtracts, and the open transfers shipping out of it with their receiving warehouse.",
  });

export const getLineSupplyDemandContract = oc
  .input(GetLineSupplyDemandInputSchema)
  .output(GetLineSupplyDemandResponseSchema)
  .route({
    method: "GET",
    summary: "Get the order lines behind a replenishment line's position",
    description:
      "For one replenishment suggestion, returns at its destination the open purchase-order and inbound transfer-order lines behind its on-order quantity, the open sales-order lines (with customer) and staged outbound transfer lines its net available subtracts, and the open transfers shipping out of it with their receiving warehouse.",
  });
